import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Clock, X } from 'lucide-react';
import { usePostiAvvisi } from './postiAvvisi';

/**
 * PilaAvvisi — i pazienti in ritardo (segreteria) e quelli in sala
 * d'attesa (medici).
 *
 * FINO A DUE: i riquadri, nell'angolo in basso a destra, come prima.
 * DA TRE IN SU: un solo pulsante nella barra in alto, «N pazienti in
 * ritardo», che si apre con un clic e mostra tutti i riquadri (Alex, 26
 * settembre 2026). La storia:
 *  - il 24 settembre la pila copriva i pulsanti della pagina (misurato sul
 *    banco: un riquadro copriva un «apri visita» su tre, due tutti e tre,
 *    con quattro ritardi la colonna arrivava al 70% dello schermo); era
 *    diventata «al massimo due, poi Leggi altri»;
 *  - ma «Leggi altri» riapriva la colonna alta sopra la pagina. Da tre in
 *    su gli avvisi vanno nella barra, che non sta sopra niente, e il
 *    pannello lo apre chi lo vuole guardare.
 *
 * Lo spazio in fondo alla pagina (--rm-spazio-pila) lo misura Layout
 * sull'angolo intero, non piu' qui: nell'angolo adesso sta anche il
 * Promemoria.
 */
const IN_ANGOLO = 2;

const PilaAvvisi = ({ voci, riassunto }) => {
    const { angolo, barra } = usePostiAvvisi();
    const [aperto, setAperto] = useState(false);
    const [alto, setAlto] = useState(0);
    const pulsante = useRef(null);
    const pannello = useRef(null);
    const nellaBarra = voci.length > IN_ANGOLO;

    /* Il pannello si apre sotto la barra, alla sua altezza vera (la barra
       e' alta 78 px sullo schermo largo, meno sul telefono). */
    const apri = () => {
        /* Sotto la BARRA, non sotto il pulsante: il pulsante sta a meta'
           della barra, e un pannello aperto da li' ne copriva il fondo. */
        const barraIntera = pulsante.current?.closest('.header') || pulsante.current;
        const r = barraIntera?.getBoundingClientRect();
        setAlto(r ? Math.round(r.bottom + 8) : 96);
        setAperto((a) => !a);
    };

    /* Si chiude cliccando fuori, o con Esc: e' un pannello aperto da chi
       lo voleva vedere, non deve restare davanti alla pagina. */
    useEffect(() => {
        if (!aperto) return undefined;
        const fuori = (e) => {
            if (pannello.current?.contains(e.target) || pulsante.current?.contains(e.target)) return;
            setAperto(false);
        };
        const esc = (e) => { if (e.key === 'Escape') setAperto(false); };
        document.addEventListener('mousedown', fuori);
        document.addEventListener('keydown', esc);
        return () => {
            document.removeEventListener('mousedown', fuori);
            document.removeEventListener('keydown', esc);
        };
    }, [aperto]);

    // Tornati a due o meno, il pannello non ha piu' senso
    useEffect(() => { if (!nellaBarra) setAperto(false); }, [nellaBarra]);

    if (!voci.length) return null;

    if (!nellaBarra) {
        return angolo ? createPortal(
            <div className="rit-pila" role="status" aria-live="polite">{voci}</div>, angolo) : null;
        // (il Promemoria, sopra, si accorcia quando c'e' la pila: Alert.css)
    }

    return barra ? createPortal(
        <>
            <button type="button" className={`rit-riassunto${aperto ? ' rit-riassunto--aperto' : ''}`}
                    ref={pulsante} onClick={apri} aria-expanded={aperto}
                    title={riassunto(voci.length)}>
                <Clock size={16} aria-hidden="true" />
                <span className="rit-riassunto-numero">{voci.length}</span>
                <span className="rit-riassunto-testo">{riassunto(voci.length).replace(/^\d+\s*/, '')}</span>
            </button>
            {aperto && (
                <div className="rit-pannello" ref={pannello} style={{ top: alto }}
                     role="dialog" aria-label={riassunto(voci.length)}>
                    <div className="rit-pannello-testa">
                        <span>{riassunto(voci.length)}</span>
                        <button type="button" className="rit-pannello-chiudi" onClick={() => setAperto(false)}
                                aria-label="Chiudi" title="Chiudi">
                            <X size={16} aria-hidden="true" />
                        </button>
                    </div>
                    <div className="rit-pannello-voci">{voci}</div>
                </div>
            )}
        </>, barra) : null;
};

export default PilaAvvisi;
